// ============================================
// Operating Modes
// ============================================

export type OperatingMode = 'production' | 'test' | 'in_progress' | 'preview';

export interface OperatingModeConfig {
  mode: OperatingMode;
  trackingEnabled: boolean;
  leadStorageEnabled: boolean;
  leadStoragePath: 'actual' | 'test' | null;
  dataSource: 'published' | 'draft';
  showModeBanner: boolean;
}

// ============================================
// Funnel Configuration
// ============================================

export type FunnelStatus =
  | 'active'
  | 'paused'
  | 'archived'
  | 'scheduled'
  | 'expired'
  | 'unpublished';

export interface LogoSettings {
  path?: string;
  showOnFirstPage?: boolean;
  showOnFinalPage?: boolean;
}

export interface FunnelConfig {
  funnelID: string;
  version: number;
  status: FunnelStatus;
  title: string;
  subtitle?: string;
  contactEmail: string;

  // Questions
  questions: Question[];
  finalPage: FinalPage;

  // Theme
  theme: Theme;

  // Smart Paths / Bounce Flow
  bounceFlow?: BounceFlow;

  // Compliance
  showAgeGate: boolean;
  showGDPRConsent: boolean;

  // Tracking
  tracking: TrackingConfig;

  // Branding
  showBranding: boolean;
  customLogo?: string;
  logoSettings?: LogoSettings;

  // Behavior
  allowEditAnswers: boolean;

  // Scheduling
  scheduledStartDate?: string;
  scheduledEndDate?: string;
}

// ============================================
// Questions
// ============================================

export interface Question {
  id: string;
  question: string;
  subtitle?: string;
  options: QuestionOption[];
  image?: string;
  video?: string;
}

export interface QuestionOption {
  value: string;
  label: string;
  text?: string;  // Legacy field - some funnels use 'text' instead of 'label'
  image?: string;
  emoji?: string;
}

// ============================================
// Final Page
// ============================================

export interface FinalPage {
  headline: string;
  subtitle?: string;
  fields: FieldType[];
  ctaText: string;
  consentText?: string;
  consentRequired?: boolean;
  disclaimer?: string;
  successTitle?: string;
  successMessage?: string;
  redirectURL?: string;
  redirectDelay?: number;
  image?: string | GraphicConfig;
  graphic?: string | GraphicConfig;
}

export type FieldType = 'name' | 'firstName' | 'lastName' | 'email' | 'phone';

export interface GraphicConfig {
  path: string;
  alt?: string;
  maxWidth?: string;
  width?: number;
  height?: number;
  align?: 'left' | 'center' | 'right';
}

// ============================================
// Theme
// ============================================

export interface Theme {
  primaryColor?: string;
  primaryDark?: string;
  primaryLight?: string;
  accentColor?: string;
  accentDark?: string;
  accentLight?: string;
  textPrimary?: string;
  textBody?: string;
  textSecondary?: string;
  gradientStart?: string;
  gradientEnd?: string;
  backgroundColor?: string;
  fontFamily?: string;
  headingFontFamily?: string;
  borderRadius?: string;
}

// ============================================
// Bounce Flow / Smart Paths
// ============================================

export interface BounceFlow {
  enabled: boolean;
  pages: SmartPathPage[];
  mappings: AnswerMappings;
  defaultPageId: string | null;
}

export interface SmartPathPage {
  id: string;
  name: string;
  type: 'soft' | 'hard';
  headline: string;
  subhead?: string;
  body?: string;
  ctaText: string;
  fields?: FieldType[];
  showConsent?: boolean;
  consentText?: string;
  successTitle?: string;
  successMessage?: string;
  redirectUrl?: string;
  ctaAction: 'capture' | 'redirect' | 'close';
}

export interface AnswerMappings {
  [questionId: string]: {
    [optionIndex: string]: string; // bouncePageId
  };
}

// ============================================
// Tracking
// ============================================

export interface TrackingConfig {
  facebookPixelID?: string;
  facebookAccessToken?: string;
  googleTagManagerID?: string;
  googleAnalyticsID?: string;
  googleAdsID?: string;
  googleAdsConversionLabel?: string;
  tiktokPixelID?: string;
}

// ============================================
// Answers
// ============================================

export interface Answer {
  questionId: string;
  questionIndex: number;
  optionIndex: number;
  optionValue: string;
  optionLabel: string;
  timestamp: number;
  timeSpent: number;
}

// ============================================
// Lead
// ============================================

export interface Lead {
  leadID: string;
  funnelID: string;
  funnelVersion: number;
  timestamp: string;
  testMode: boolean;

  // Contact info
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;

  // Consent
  contactConsent: boolean;
  consentText?: string | null;

  // Quiz answers
  answers: Record<string, Answer>;

  // Tracking
  utmParams: UTMParams;
  customParams: Record<string, string>;
  referrerInfo: ReferrerInfo;

  // Facebook
  fbc?: string | null;
  fbp?: string | null;

  // GDPR
  gdprConsent: GDPRConsent | null;

  // Metadata
  userAgent: string;
  screenResolution: string;
  language: string;
  timezone: string;
}

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

export interface ReferrerInfo {
  referrer: string;
  landingPage: string;
}

export interface GDPRConsent {
  consented: boolean;
  timestamp: string;
  type: 'marketing' | 'necessary';
}

// ============================================
// App State
// ============================================

export interface AppState {
  operatingMode: OperatingMode;
  funnel: FunnelConfig | null;
  funnelStatus: FunnelStatus | null;
  loadError: string | null;

  quiz: QuizState;
  smartPaths: SmartPathState;
  form: FormState;
  compliance: ComplianceState;
  session: SessionState;
  ui: UIState;
}

export interface QuizState {
  currentQuestionIndex: number;
  answers: Record<string, Answer>;
  isComplete: boolean;
  startedAt: number | null;
  completedAt: number | null;
}

export interface SmartPathState {
  activePathId: string | null;
  visitedPaths: string[];
  completedPaths: string[];
}

export interface FormState {
  fields: Record<string, string>;
  errors: Record<string, string>;
  isSubmitting: boolean;
  isSubmitted: boolean;
  leadId: string | null;
}

export interface ComplianceState {
  ageVerified: boolean;
  ageVerifiedAt: number | null;
  gdprConsent: GDPRConsent | null;
  gdprConsentAt: number | null;
}

export interface SessionState {
  sessionId: string;
  leadId: string | null;
  utmParams: UTMParams;
  customParams: Record<string, string>;
  referrer: string | null;
  userAgent: string;
}

export interface UIState {
  isLoading: boolean;
  showAgeGate: boolean;
  showGDPRModal: boolean;
  showModeBanner: boolean;
  showSmartPath: boolean;
}

// ============================================
// API Responses
// ============================================

export interface FunnelLoadResponse {
  success: boolean;
  funnel?: FunnelConfig;
  error?: {
    code: string;
    message: string;
    status?: FunnelStatus;
  };
}

export interface LeadSubmitResponse {
  success: boolean;
  leadID?: string;
  error?: string;
  result?: Record<string, unknown>;
}

// ============================================
// Logger
// ============================================

export enum LogLevel {
  OFF = 0,
  ERROR = 1,
  WARN = 2,
  INFO = 3,
  DEBUG = 4,
  TRACE = 5
}

export interface LogEntry {
  level: LogLevel;
  module: string;
  message: string;
  data?: unknown;
  timestamp: Date;
}

// ============================================
// Events
// ============================================

export type QuizEvent =
  | 'quiz:start'
  | 'quiz:progress'
  | 'quiz:complete'
  | 'quiz:reset';

export type SmartPathEvent =
  | 'smartpath:show'
  | 'smartpath:form:start'
  | 'smartpath:form:submit'
  | 'smartpath:redirect'
  | 'smartpath:close';

export type FormEvent =
  | 'form:start'
  | 'form:field:change'
  | 'form:submit'
  | 'form:success'
  | 'form:error';

// ============================================
// Server Progress Tracking
// ============================================

export type ProgressEventType =
  | 'quiz_started'
  | 'question_answered'
  | 'quiz_completed'
  | 'smart_path_shown'
  | 'smart_path_action'
  | 'form_started'
  | 'lead_submitted';

export interface ProgressEvent {
  type: ProgressEventType;
  sessionId: string;
  funnelId: string;
  timestamp: number;
  testMode: boolean;
  data?: ProgressEventData;
}

export type ProgressEventData =
  | QuestionAnsweredData
  | QuizCompletedData
  | SmartPathShownData
  | SmartPathActionData
  | LeadSubmittedData;

export interface QuestionAnsweredData {
  questionId: string;
  questionIndex: number;
  optionIndex: number;
  optionValue: string;
  timeSpent: number;
}

export interface QuizCompletedData {
  totalQuestions: number;
  totalTime: number;
}

export interface SmartPathShownData {
  pathId: string;
  pathType: 'soft' | 'hard';
  triggeredBy: {
    questionId: string;
    optionIndex: number;
  };
}

export interface SmartPathActionData {
  pathId: string;
  action: 'submit' | 'redirect' | 'close';
}

export interface LeadSubmittedData {
  leadId: string;
}
